
import { mapGetters, mapState, mapMutations } from "vuex";
import EventBus from "@/event-bus";
import { PortalAdapterApi } from "@vacancorp/portal.adapter.api";
import MapsMixin from "@/mixins/maps";

let LngOffset = 0;

export default MapsMixin.extend({
  name: "component-google-maps",
  mixins: [MapsMixin],
  computed: {
    ...mapState("data", ["isFirstLoadMap", "selectedPlaceIdHash", "gMapsReady", "currentPositionMarker"]),
    ...mapState("data", ["gMaps", "selectedPlaceIdHash"]),
    ...mapGetters("ui", ["isMobile", "getBottomSectionOffsetTop"]),
    bottomSectionOffsetTop(): number | undefined {
      return this.getBottomSectionOffsetTop;
    },
  },

  watch: {
    bottomSectionOffsetTop() {
      this.setGoogleLogoPosition();
    },
  },

  mounted() {
    this.initGoogleMaps(this.isMobile);
    google.maps.event.addListener(this.gMaps, "dragstart", this.handleGoogleMapDrag);
    google.maps.event.addListener(this.gMaps, "click", this.handleGoogleMapClick);
    google.maps.event.addListener(this.gMaps, "zoom_changed", this.handleGoogleMapZoomChanged);
    // triggered once when google-map loaded completely
    google.maps.event.addListenerOnce(this.gMaps, "idle", this.setupGoogleMapStyle);
  },

  beforeDestroy() {
    google.maps.event.clearListeners(this.gMaps, "dragstart");
    google.maps.event.clearListeners(this.gMaps, "click");
    google.maps.event.clearListeners(this.gMaps, "zoom_changed");
    google.maps.event.clearListeners(this.gMaps, "idle");

    this.$store.commit("data/setGMaps", undefined);
  },

  methods: {
    ...mapMutations("data", ["setMapsZoom", "setCurrentPositionMarker", "set"]),

    /** @remarks mixins for outbound components */
    moveMapTo(location: PortalAdapterApi.Location) {
      if (!this.gMaps) return;
      this.gMaps?.panTo(new google.maps.LatLng(location.lat, location.lng));
    },
    boundMap(latlngs: PortalAdapterApi.Location[]) {
      // need to wait a little
      var bounds = new google.maps.LatLngBounds();
      latlngs.forEach((latlng) => {
        bounds.extend(latlng);
      });
      setTimeout(() => {
        this.gMaps?.fitBounds(bounds);
      }, 100);
    },

    /** @remarks init google maps */
    initGoogleMaps(isMobile = true) {
      if (this.gMaps !== undefined) return;
      const mapElement: HTMLElement | null = document.getElementById("map");
      if (mapElement === null) return;

      this.$store.commit(
        "data/setGMaps",
        new google.maps.Map(mapElement, {
          center: { lat: 35.6870985, lng: 139.764569 },
          draggable: true,
          zoom: 13,
          minZoom: 8,
          mapTypeControl: false,
          zoomControl: false,
          scaleControl: false,
          disableDefaultUI: true,
          clickableIcons: false,
          gestureHandling: "greedy",
          keyboardShortcuts: !isMobile,
          styles: [
            {
              featureType: "administrative",
              elementType: "all",
              stylers: [
                {
                  visibility: "on",
                },
              ],
            },
            {
              featureType: "administrative",
              elementType: "labels",
              stylers: [
                {
                  visibility: "off",
                },
              ],
            },
            {
              featureType: "administrative",
              elementType: "labels.text",
              stylers: [
                {
                  visibility: "off",
                },
              ],
            },
            {
              featureType: "administrative.country",
              elementType: "all",
              stylers: [
                {
                  visibility: "on",
                },
              ],
            },
            {
              featureType: "administrative.country",
              elementType: "geometry",
              stylers: [
                {
                  visibility: "simplified",
                },
                {
                  hue: "#ff0000",
                },
              ],
            },
            {
              featureType: "administrative.country",
              elementType: "labels.text",
              stylers: [
                {
                  visibility: "off",
                },
              ],
            },
            {
              featureType: "administrative.province",
              elementType: "all",
              stylers: [
                {
                  visibility: "on",
                },
              ],
            },
            {
              featureType: "administrative.locality",
              elementType: "all",
              stylers: [
                {
                  visibility: "on",
                },
              ],
            },
            {
              featureType: "landscape",
              elementType: "labels.text",
              stylers: [
                {
                  visibility: "off",
                },
              ],
            },
            {
              featureType: "landscape.man_made",
              elementType: "geometry.fill",
              stylers: [
                {
                  lightness: "0",
                },
                {
                  saturation: "-55",
                },
              ],
            },
            {
              featureType: "landscape.man_made",
              elementType: "geometry.stroke",
              stylers: [
                {
                  color: "#ffffff",
                },
                {
                  visibility: "off",
                },
              ],
            },
            {
              featureType: "poi",
              elementType: "all",
              stylers: [
                {
                  lightness: "20",
                },
                {
                  saturation: "-72",
                },
              ],
            },
            {
              featureType: "poi",
              elementType: "labels",
              stylers: [
                {
                  lightness: "0",
                },
              ],
            },
            {
              featureType: "poi",
              elementType: "labels.text",
              stylers: [
                {
                  lightness: "0",
                },
              ],
            },
            {
              featureType: "poi",
              elementType: "labels.text.fill",
              stylers: [
                {
                  lightness: "-15",
                },
              ],
            },
            {
              featureType: "poi",
              elementType: "labels.text.stroke",
              stylers: [
                {
                  lightness: "-5",
                },
              ],
            },
            {
              featureType: "poi",
              elementType: "labels.icon",
              stylers: [
                {
                  lightness: "-5",
                },
              ],
            },
            {
              featureType: "poi.attraction",
              elementType: "labels",
              stylers: [
                {
                  lightness: "0",
                },
              ],
            },
            {
              featureType: "poi.attraction",
              elementType: "labels.icon",
              stylers: [
                {
                  lightness: "25",
                },
              ],
            },
            {
              featureType: "poi.business",
              elementType: "labels.icon",
              stylers: [
                {
                  lightness: "15",
                },
              ],
            },
            {
              featureType: "road",
              elementType: "all",
              stylers: [
                {
                  lightness: "-4",
                },
                {
                  visibility: "on",
                },
              ],
            },
            {
              featureType: "road",
              elementType: "geometry.stroke",
              stylers: [
                {
                  visibility: "off",
                },
              ],
            },
            {
              featureType: "road",
              elementType: "labels",
              stylers: [
                {
                  visibility: "off",
                },
              ],
            },
            {
              featureType: "road",
              elementType: "labels.text",
              stylers: [
                {
                  visibility: "off",
                },
              ],
            },
            {
              featureType: "road.highway",
              elementType: "all",
              stylers: [
                {
                  visibility: "on",
                },
              ],
            },
            {
              featureType: "road.highway",
              elementType: "geometry.fill",
              stylers: [
                {
                  lightness: "53",
                },
                {
                  visibility: "on",
                },
                {
                  saturation: "-55",
                },
                {
                  color: "#c9c9c9",
                },
              ],
            },
            {
              featureType: "road.highway",
              elementType: "geometry.stroke",
              stylers: [
                {
                  color: "#dadae4",
                },
              ],
            },
            {
              featureType: "road.highway.controlled_access",
              elementType: "all",
              stylers: [
                {
                  visibility: "on",
                },
                {
                  saturation: "-100",
                },
                {
                  lightness: "54",
                },
              ],
            },
            {
              featureType: "road.arterial",
              elementType: "geometry.fill",
              stylers: [
                {
                  lightness: "100",
                },
                {
                  visibility: "on",
                },
              ],
            },
            {
              featureType: "road.local",
              elementType: "geometry.fill",
              stylers: [
                {
                  lightness: "100",
                },
              ],
            },
            {
              featureType: "road.local",
              elementType: "geometry.stroke",
              stylers: [
                {
                  color: "#efeff5",
                },
                {
                  visibility: "off",
                },
              ],
            },
            {
              featureType: "transit",
              elementType: "labels",
              stylers: [
                {
                  visibility: "on",
                },
              ],
            },
            {
              featureType: "transit.line",
              elementType: "all",
              stylers: [
                {
                  saturation: "-80",
                },
                {
                  lightness: "20",
                },
              ],
            },
            {
              featureType: "transit.station.rail",
              elementType: "all",
              stylers: [
                {
                  saturation: "0",
                },
              ],
            },
            {
              featureType: "water",
              elementType: "geometry.fill",
              stylers: [
                {
                  color: "#c5d7ec",
                },
              ],
            },
          ] as google.maps.MapTypeStyle[],
        }),
      );
    },

    /** @remarks google maps actions */
    moveMapToCurrentLocation(payload: {
      location: PortalAdapterApi.Location;
      moveToCurrentPlace: boolean;
      icon: google.maps.Icon;
    }) {
      if (payload?.location?.lat === undefined) return;
      if (this.gMapsReady) {
        // Remove mark of current location
        if (this.currentPositionMarker !== undefined) {
          this.currentPositionMarker.setMap(null);
        }
        // add icon of current location
        const currentMarker: google.maps.Marker = new google.maps.Marker({
          position: { lat: payload.location.lat, lng: payload.location.lng },
          map: this.gMaps,
          icon: payload.icon,
        });
        this.setCurrentPositionMarker(currentMarker);
        this.currentPositionMarker.setZIndex(9999);
        // move maps to current loation
        if (payload.moveToCurrentPlace) {
          if (!this.gMaps) return;
          this.gMaps?.panTo(new google.maps.LatLng(payload.location.lat, payload.location.lng));
          payload.moveToCurrentPlace = false;
        }
      }
    },
    radiusFromCenter() {
      const metersPerPixal = this.$store.getters["data/metersPerPixal"](this.mapsZoom);
      return this.$store.getters["data/radiusFromCenter"]({
        metersPerPixal: metersPerPixal,
        windowWidth: window.innerWidth,
      });
    },
    setMapCenter({ lat: inputLat, lng: inputLng }: PortalAdapterApi.Location) {
      const metersPerPixal = this.$store.getters["data/metersPerPixal"](this.mapsZoom);
      LngOffset = -metersPerPixal / 1000;
      if (!this.gMaps) return;
      console.log(`--- set to Map Center ---`);
      if (this.isMobile) {
        this.gMaps?.panTo(new google.maps.LatLng(inputLat + LngOffset, inputLng));
      } else {
        this.gMaps?.panTo(new google.maps.LatLng(inputLat, inputLng));
      }
    },
    moveMapsToSelectedMarker(placeIdHash: string) {
      // Move maps
      const locationOfPlace: PortalAdapterApi.Location | undefined =
        this.$store.getters["data/getLocationOfPlace"](placeIdHash);
      if (locationOfPlace === undefined) return;

      const metersPerPixal = this.$store.getters["data/metersPerPixal"](this.mapsZoom);
      LngOffset = -metersPerPixal / 1000;

      if (!this.gMaps) return;
      console.log(`--- move Maps to selected marker ---`);
      if (this.isMobile) {
        this.gMaps?.panTo(new google.maps.LatLng(locationOfPlace.lat + LngOffset, locationOfPlace.lng));
      } else {
        this.gMaps?.panTo(new google.maps.LatLng(locationOfPlace.lat, locationOfPlace.lng));
      }
    },
    /** @remarks google maps events */
    handleGoogleMapDrag() {
      EventBus.$emit("change-gMap-in-action", true);
      EventBus.$emit("search-again", true);
    },
    handleGoogleMapClick(_: google.maps.MouseEvent) {
      EventBus.$emit("change-gMap-in-action", true);
    },
    handleGoogleMapZoomChanged() {
      if (this.gMaps?.getZoom() === this.mapsZoom) return;
      this.setMapsZoom(this.gMaps?.getZoom());
      EventBus.$emit("search-again", true);
    },

    /** @remarks Customize google logo to left-top of cards */
    setGoogleLogoPosition() {
      const mapElement = this.$refs.map as (HTMLElement & Vue) | undefined;
      if (!mapElement) return;

      const googleLogo: HTMLElement | null = mapElement.querySelector("img[alt='Google']");
      if (googleLogo === null) {
        setTimeout(() => {
          this.setGoogleLogoPosition();
        }, 1000);
        return;
      }

      if (this.bottomSectionOffsetTop === undefined) {
        googleLogo.style.top = "";
        googleLogo.style.bottom = "0px";
        googleLogo.style.left = "5px";
        googleLogo.style.position = "fixed";
        googleLogo.style.transitionDuration = "0.4s";
        return;
      }

      const offsetTop = this.bottomSectionOffsetTop - 20;
      googleLogo.style.top = `${offsetTop}px`;
      googleLogo.style.bottom = "";
      googleLogo.style.left = "5px";
      googleLogo.style.position = "fixed";
      googleLogo.style.transitionDuration = "0.4s";
    },

    /**
     * @remarks default position of the google maps api is relative
     * for iOS toolbar covered issue, css position should be a 'fixed'
     * after google maps api loaded completely
     */
    setupGoogleMapStyle() {
      const mapElement: HTMLElement | null = this.$refs.map as HTMLElement | null;
      if (mapElement === null) return;
      mapElement.style.position = "fixed";
      mapElement.style.width = "100%";
      mapElement.style.height = "100%";
      if (!this.isMobile) mapElement.style.width = "";
      // mark for internal events
      this.$store.commit("data/setGMapsReady", true);
      if (this.isMobile) this.setGoogleLogoPosition();
    },
  },
});
